import type { TeamFormation } from './types'

/**
 * Group stage structure math for the Create tournament form.
 *
 * Mirrors the backend (beta-backend src/utils/groupPlayUtils.ts):
 * - `autoGroupStructure` = findOptimalStructure(n, { min 8, max 10, play-in on }),
 *   which is what the backend runs at start when the organizer leaves the
 *   structure on Automatic (the model's default teamsPerGroup is 8).
 * - `manualGroupStructure` = buildManualStructure + validateManualGroupShape:
 *   teams spread evenly over the chosen groups (first groups get the extra team).
 * Keep the limits below in sync with GROUP_SIZE_MIN / GROUP_SIZE_MAX / MAX_GROUPS.
 */

export const GROUP_SIZE_MIN = 2
export const GROUP_SIZE_MAX = 32
export const MAX_GROUPS = 64
export const MAX_BRACKET = 128

export type GroupStructureMode = 'auto' | 'manual'

export type GroupStructure = {
  numberOfGroups: number
  /** Team count per group (first groups get the extra team when uneven). */
  groupSizes: number[]
  advancePerGroup: number
  bracketSize: number
  /** Bracket spots filled by the play-in round (auto mode only). */
  playInSpots: number
  /** Empty bracket spots → first-round byes for the top seeds. */
  byes: number
}

/** Smallest valid bracket (power of 2, at least 4) that fits `n` teams. */
export const bracketSizeFor = (n: number) => Math.max(4, Math.pow(2, Math.ceil(Math.log2(Math.max(1, n)))))

export const isPowerOfTwo = (n: number) => n > 0 && (n & (n - 1)) === 0

export const splitTeams = (teamCount: number, groups: number): number[] => {
  const base = Math.floor(teamCount / groups)
  const remainder = teamCount % groups
  return Array.from({ length: groups }, (_, i) => base + (i < remainder ? 1 : 0))
}

/* -------------------------------------------------------------------------- */
/* Auto (backend findOptimalStructure port)                                   */
/* -------------------------------------------------------------------------- */

const calculateStructure = (teamCount: number, preferredTeamsPerGroup: number, playInEnabled: boolean) => {
  let bracketSize = Math.pow(2, Math.floor(Math.log2(teamCount * 0.65)))
  bracketSize = Math.min(128, Math.max(4, bracketSize))
  while (bracketSize > teamCount) bracketSize = bracketSize / 2

  const numberOfGroups = Math.max(2, Math.round(teamCount / preferredTeamsPerGroup))
  const groupSizes = splitTeams(teamCount, numberOfGroups)
  const advancePerGroup = Math.floor(bracketSize / numberOfGroups)
  const advanceRemainder = bracketSize % numberOfGroups
  let playInSpots = 0
  let playInTeamsPerGroup = 0
  if (playInEnabled && advanceRemainder > 0) {
    playInSpots = advanceRemainder
    playInTeamsPerGroup = Math.ceil((playInSpots * 2) / numberOfGroups)
  }
  const eliminatedPerGroup = groupSizes.map((c) => Math.max(0, c - advancePerGroup - playInTeamsPerGroup))

  let warnings = 0
  if (bracketSize > teamCount) warnings++
  if (advancePerGroup * numberOfGroups + playInSpots !== bracketSize) warnings++
  if (Math.max(...groupSizes) - Math.min(...groupSizes) > 1) warnings++

  return { numberOfGroups, groupSizes, advancePerGroup, bracketSize, playInSpots, eliminatedPerGroup, warnings }
}

/** The structure the backend picks on its own for `teamCount` teams (null below 4 teams). */
export const autoGroupStructure = (teamCount: number, playInEnabled = true): GroupStructure | null => {
  if (!Number.isFinite(teamCount) || teamCount < 4) return null
  let best: ReturnType<typeof calculateStructure> | null = null
  let bestScore = -Infinity
  for (let tpg = 8; tpg <= 10; tpg++) {
    const s = calculateStructure(teamCount, tpg, playInEnabled)
    let score = 0
    if (s.warnings === 0) score += 100
    score -= (Math.max(...s.groupSizes) - Math.min(...s.groupSizes)) * 10
    score -= Math.abs(teamCount / s.numberOfGroups - 7.5) * 5
    const eliminationRate = s.eliminatedPerGroup.reduce((a, b) => a + b, 0) / teamCount
    if (eliminationRate > 0.1 && eliminationRate < 0.4) score += 20
    score -= s.warnings * 15
    if (score > bestScore) {
      bestScore = score
      best = s
    }
  }
  if (!best) return null
  const filled = best.advancePerGroup * best.numberOfGroups + best.playInSpots
  return {
    numberOfGroups: best.numberOfGroups,
    groupSizes: best.groupSizes,
    advancePerGroup: best.advancePerGroup,
    bracketSize: best.bracketSize,
    playInSpots: best.playInSpots,
    byes: Math.max(0, best.bracketSize - filled),
  }
}

/* -------------------------------------------------------------------------- */
/* Manual                                                                     */
/* -------------------------------------------------------------------------- */

export type ManualCheck = { structure: GroupStructure | null; error: string | null }

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * The organizer's chosen shape. `teamCount` is the planned capacity for team
 * signup, or null for solo signup (team count unknown until signups close).
 * Returns the error the backend would answer with, so the form can never save
 * a structure the backend can't run.
 */
export const manualGroupStructure = ({
  teamCount,
  numberOfGroups,
  advancePerGroup,
  doubleElim,
}: {
  teamCount: number | null
  numberOfGroups: number
  advancePerGroup: number
  doubleElim: boolean
}): ManualCheck => {
  const total = numberOfGroups * advancePerGroup
  const bracketSize = bracketSizeFor(total)
  const groupSizes = teamCount ? splitTeams(teamCount, numberOfGroups) : []
  const structure: GroupStructure = {
    numberOfGroups,
    groupSizes,
    advancePerGroup,
    bracketSize,
    playInSpots: 0,
    byes: bracketSize - total,
  }

  let error: string | null = null
  if (!Number.isInteger(numberOfGroups) || numberOfGroups < 1) error = 'Pick at least 1 group.'
  else if (numberOfGroups > MAX_GROUPS) error = `Use ${MAX_GROUPS} groups or fewer.`
  else if (!Number.isInteger(advancePerGroup) || advancePerGroup < 1) error = 'At least 1 team has to advance from each group.'
  else if (total < 2) error = 'At least 2 teams must reach the bracket. Add a group or let more teams advance.'
  else if (total > MAX_BRACKET) error = `${total} teams would advance, but a bracket holds at most ${MAX_BRACKET}. Let fewer teams advance.`
  else if (teamCount) {
    const smallest = Math.min(...groupSizes)
    const largest = Math.max(...groupSizes)
    if (numberOfGroups * GROUP_SIZE_MIN > teamCount) {
      error = `${teamCount} teams can’t fill ${plural(numberOfGroups, 'group')}. Use ${Math.floor(teamCount / GROUP_SIZE_MIN)} or fewer.`
    } else if (largest > GROUP_SIZE_MAX) {
      error = `Groups can hold at most ${GROUP_SIZE_MAX} teams. Use at least ${Math.ceil(teamCount / GROUP_SIZE_MAX)} groups.`
    } else if (advancePerGroup >= smallest) {
      error = `The smallest group has ${smallest} teams, so at most ${smallest - 1} can advance from each group.`
    }
  }
  if (!error && doubleElim && structure.byes > 0) {
    error = `Double elimination needs a full bracket: ${plural(numberOfGroups, 'group')} × ${advancePerGroup} advancing = ${total}, but brackets come in 4, 8, 16, 32… Change the numbers or use single elimination (top seeds get byes).`
  }
  return { structure: error ? null : structure, error }
}

/** Quick-pick chips: every even split of `teamCount` into groups of 2–32. */
export const evenSplits = (teamCount: number): { groups: number; size: number }[] => {
  const out: { groups: number; size: number }[] = []
  for (let g = 1; g <= Math.min(MAX_GROUPS, teamCount); g++) {
    if (teamCount % g !== 0) continue
    const size = teamCount / g
    if (size < 3 || size > GROUP_SIZE_MAX) continue // groups of 2 are allowed via the stepper, not suggested
    out.push({ groups: g, size })
  }
  return out
}

/** Bounds for the "Number of groups" stepper. */
export const groupCountRange = (teamCount: number | null) =>
  teamCount
    ? { min: Math.max(1, Math.ceil(teamCount / GROUP_SIZE_MAX)), max: Math.max(1, Math.min(MAX_GROUPS, Math.floor(teamCount / GROUP_SIZE_MIN))) }
    : { min: 1, max: 16 }

/** Bounds for the "Advancing per group" stepper. */
export const advanceRange = (teamCount: number | null, numberOfGroups: number) => {
  const cap = Math.max(1, Math.floor(MAX_BRACKET / Math.max(1, numberOfGroups)))
  if (!teamCount) return { min: 1, max: Math.min(16, cap) }
  const smallest = Math.floor(teamCount / Math.max(1, numberOfGroups))
  return { min: 1, max: Math.max(1, Math.min(smallest - 1, cap)) }
}

/** "groups of 8" / "groups of 6 and 5" / "1 group of 16". */
export const describeGroupSizes = (sizes: number[]) => {
  if (sizes.length === 0) return ''
  const big = Math.max(...sizes)
  const small = Math.min(...sizes)
  if (sizes.length === 1) return `1 group of ${big}`
  return big === small ? `${sizes.length} groups of ${big}` : `${sizes.length} groups of ${big} and ${small}`
}

/** Round-robin load: matches each team plays in the largest group, and total group matches. */
export const roundRobinLoad = (sizes: number[]) => ({
  perTeam: sizes.length ? Math.max(...sizes) - 1 : 0,
  total: sizes.reduce((sum, n) => sum + (n * (n - 1)) / 2, 0),
})

/* -------------------------------------------------------------------------- */
/* Summary used by the live preview and Review step                           */
/* -------------------------------------------------------------------------- */

export type GroupStageInput = {
  structureMode: GroupStructureMode
  numberOfGroups?: number
  advancePerGroup?: number
  playInEnabled: boolean
}

export type GroupStageSummary = {
  /** Full sentence for the Review step. */
  text: string
  /** Compact value for the live preview column. */
  short: string
  error: string | null
}

const sizesShort = (sizes: number[]) => {
  const big = Math.max(...sizes)
  const small = Math.min(...sizes)
  return big === small ? `${sizes.length} × ${big}` : `${sizes.length} × ${big}/${small}`
}

export const groupStageSummary = ({
  config,
  teamFormation,
  numberOfTeams,
  playFormat,
}: {
  config: GroupStageInput
  teamFormation: TeamFormation
  numberOfTeams?: string
  playFormat?: string
}): GroupStageSummary => {
  const solo = teamFormation !== 'teams'
  const teamCount = solo ? null : parseInt(numberOfTeams || '') || null

  if (config.structureMode === 'manual' && config.numberOfGroups && config.advancePerGroup) {
    const { structure, error } = manualGroupStructure({
      teamCount,
      numberOfGroups: config.numberOfGroups,
      advancePerGroup: config.advancePerGroup,
      doubleElim: playFormat === 'double_elimination',
    })
    const groupsWord = plural(config.numberOfGroups, 'group')
    if (error || !structure) {
      return { text: `${groupsWord} · top ${config.advancePerGroup} advance`, short: 'Needs a fix', error }
    }
    const groups = solo ? groupsWord : describeGroupSizes(structure.groupSizes)
    const byes = structure.byes > 0 ? ` (${plural(structure.byes, 'bye')})` : ''
    return {
      text: `${groups} · top ${structure.advancePerGroup} of each advance → bracket of ${structure.bracketSize}${byes}`,
      short: `${solo ? groupsWord : sizesShort(structure.groupSizes)} · top ${structure.advancePerGroup} → ${structure.bracketSize}`,
      error: null,
    }
  }

  if (solo || !teamCount) {
    return { text: 'Automatic · worked out when signups close', short: 'Automatic', error: null }
  }
  const auto = autoGroupStructure(teamCount, config.playInEnabled)
  if (!auto) return { text: 'Automatic', short: 'Automatic', error: null }
  const playIn = auto.playInSpots > 0 ? ` + ${auto.playInSpots} via play-in` : ''
  return {
    text: `Automatic · for ${teamCount} teams: ${describeGroupSizes(auto.groupSizes)} · top ${auto.advancePerGroup}${playIn} → bracket of ${auto.bracketSize}`,
    short: `Auto · ${sizesShort(auto.groupSizes)} → ${auto.bracketSize}`,
    error: null,
  }
}
